import { useCallback, useMemo, useState } from "react";
import {
  useConnectionPreviewViewModel,
  type ConnectionPreviewTask,
  type ConnectionPreviewTaskState,
} from "@onetasklist/shared";
import { useApi } from "@api/useApi";
import type {
  ConnectionPreviewBadgeTone,
  ConnectionPreviewCount,
  ConnectionPreviewDockViewModel,
  ConnectionPreviewDockViewModelProps,
  ConnectionPreviewRow,
} from "./ConnectionPreviewDock.ViewModel.types";

const STATE_TONES: Record<
  ConnectionPreviewTaskState,
  ConnectionPreviewBadgeTone
> = {
  New: "brand",
  Existing: "informative",
  Archived: "warning",
  Removed: "danger",
  SkippedByAgeLimit: "subtle",
};

const buildSubtitle = (task: ConnectionPreviewTask): string =>
  [task.userFriendlyId, task.status].filter(Boolean).join(" · ");

export const useConnectionPreviewDockViewModel = ({
  boardId,
  draftConnection,
  loadErrorMessage,
  debounceMs,
}: ConnectionPreviewDockViewModelProps): ConnectionPreviewDockViewModel => {
  const api = useApi();
  const [isExpanded, setIsExpanded] = useState(false);

  // Nothing can be fetched before the connection is authorised — the source has no
  // credentials to call the external API with.
  const isAvailable = !!boardId && !!draftConnection.id && draftConnection.isAuthorised;

  const preview = useConnectionPreviewViewModel({
    boardId,
    connectionId: draftConnection.id,
    configuration: draftConnection.configuration,
    boardsApi: api.boards,
    enabled: isAvailable,
    debounceMs,
    loadErrorMessage,
  });

  const toggleExpanded = useCallback((): void => {
    setIsExpanded((previous) => !previous);
  }, []);

  const counts = useMemo<ConnectionPreviewCount[]>(
    () =>
      (
        [
          { key: "newLabel", value: preview.newTasks },
          { key: "existingLabel", value: preview.existingTasks },
          { key: "archivedLabel", value: preview.archivedTasks },
          { key: "removedLabel", value: preview.removedTasks },
          { key: "skippedLabel", value: preview.skippedByAgeLimitTasks },
        ] as ConnectionPreviewCount[]
      ).filter((count) => count.value > 0),
    [
      preview.archivedTasks,
      preview.existingTasks,
      preview.newTasks,
      preview.removedTasks,
      preview.skippedByAgeLimitTasks,
    ],
  );

  const rows = useMemo<ConnectionPreviewRow[]>(
    () =>
      preview.tasks.map((task, index) => ({
        key: task.sourceId || `${index}`,
        title: task.title,
        url: task.url,
        subtitle: buildSubtitle(task),
        state: task.state,
        tone: STATE_TONES[task.state] ?? "subtle",
      })),
    [preview.tasks],
  );

  return {
    isAvailable,
    isExpanded,
    toggleExpanded,
    isLoading: preview.isLoading,
    isPending: preview.isPending,
    hasResult: preview.hasResult,
    hasError: preview.hasError,
    errorMessage: preview.errorMessage,
    isInactive: preview.isInactive,
    isEmpty: preview.hasResult && !preview.hasError && !preview.isInactive && preview.totalTasks === 0,
    isTruncated: preview.isTruncated,
    totalTasks: preview.totalTasks,
    newTasks: preview.newTasks,
    counts,
    rows,
    refresh: preview.refresh,
  };
};
